import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Form, FormGroup, Label, Col, Input, Row, FormFeedback, Tooltip } from 'reactstrap';
import { withTranslation } from 'react-i18next';
import Cards from 'react-credit-cards';
import { SLIDE_UP } from 'react-ladda';
import { FaRegTrashAlt } from 'react-icons/fa';
import { getAccountId, getUserName } from '../../localStorage';
import { cardTokenFields, urlGetCreditCard } from '../../constantsApp';
import { getDataCardFromURL } from '../../utils/utils';
import { ButtonCustom, ModalAccept } from '../common';
import createPlaceToPayTokenize from '../../api/payments/createPlaceToPayTokenize';
import verifyPlaceToPaySession from '../../api/payments/verifyPlaceToPaySession';
import invalidatePlaceToPayToken from '../../api/payments/invalidatePlaceToPayToken';

const P2P_TOKENIZE_KEY = 'p2p_tokenize_pending';

class ManageCreditCard extends Component {
  p2pResumeFired = false;

  constructor() {
    super();
    this.state = {
      isSubmitting: false,
      hadPressSubmit: false,
      account: {},
      cardToken: '',
      cardExpiry: '',
      last4CC: '',
      isTokenFromURL: false,
      tooltipOpen: false,
      focusCard: '',
      isOpenModalDelete: false,
      cardTokenFromAPI: '',
      isTokenFromPlaceToPay: false,
      merchantToSave: '',
    };
  }

  componentDidMount() {
    this._isMounted = true; // track that this instance is alive
    const {
      history: {
        location: { search },
      },
    } = this.props;
    let token = '';
    let expiryDate = '';
    let last4 = '';
    if (search) {
      token = getDataCardFromURL(search, cardTokenFields.token);
      expiryDate = decodeURIComponent(getDataCardFromURL(search, cardTokenFields.expiryDate));
      last4 = getDataCardFromURL(search, cardTokenFields.last4cc);
    }
    if (token && expiryDate && last4) {
      this.setState({
        cardToken: token,
        cardExpiry: expiryDate,
        last4CC: last4,
        isTokenFromURL: true,
      });
    }

    this.resumePlaceToPayTokenizeIfPending();
  }

  componentWillUnmount() {
    this._isMounted = false; // Mark this instance as dead
  }

  checkAndSubmitIfReady = () => {
    const { account } = this.props;
    const { cardToken, merchantToSave } = this.state;

    // Wait for Redux to populate the address
    const hasAccountData = account && account.addresses && account.addresses.length > 0;
    // Wait for the PlaceToPay token
    const hasTokenData = merchantToSave === 'PLACETOPAY' && cardToken;

    if (hasAccountData && hasTokenData) {
      this.handleSubmit();
      // Drop the shield so future Redux updates don't trigger duplicate submits
      this.setState({ merchantToSave: '', isTokenFromPlaceToPay: false });
    }
  };

  applyVerifyResult = (verifyResult) => {
    if (verifyResult && verifyResult.successful && verifyResult.paymentMethodToken) {
      this.setState({
        cardToken: verifyResult.paymentMethodToken,
        cardExpiry: verifyResult.expirationDate || '12/99',
        last4CC: verifyResult.last4 || '0000',
        merchantToSave: 'PLACETOPAY',
        isTokenFromPlaceToPay: true,
      }, () => {
        this.checkAndSubmitIfReady();
      });
    } else {
      alert('PlaceToPay session was not approved or no token was generated.');
      this.setState({ isSubmitting: false, isTokenFromPlaceToPay: false });
    }
  };

  /**
   * Checks if the user just returned from Evertec's Lightbox.
   * If so, verifies the session and saves the PlaceToPay token.
   */
  resumePlaceToPayTokenizeIfPending = () => {
    if (this.p2pResumeFired) return;

    let pending = null;
    try {
      const raw = sessionStorage.getItem(P2P_TOKENIZE_KEY);
      if (raw) pending = JSON.parse(raw);
      // Clear immediately before async operations
      if (raw) sessionStorage.removeItem(P2P_TOKENIZE_KEY);
    } catch (e) { }

    if (!pending || !pending.requestId) return;

    this.p2pResumeFired = true;

    // Raise shield
    this.setState({ isSubmitting: true, isTokenFromPlaceToPay: true });

    verifyPlaceToPaySession(pending.requestId)
      .then((verifyResult) => {
        if (verifyResult && verifyResult.successful && verifyResult.paymentMethodToken) {

          // Populate UI
          this.setState({
            cardToken: verifyResult.paymentMethodToken,
            cardExpiry: verifyResult.expirationDate || '12/99',
            last4CC: verifyResult.last4 || '0000',
            merchantToSave: 'PLACETOPAY',
          });

          // Grab the existing payment profile ID
          const existingProfileId = this.props.account?.paymentProfiles?.[0]?.id || 'PRIMARY';

          // modifyAccount payload structure
          const payload = {
            id: getAccountId(),
            paymentProfiles: [
              {
                id: existingProfileId,
                paymentMethod: 'CREDIT_CARD',
                creditCards: [
                  {
                    cardToken: verifyResult.paymentMethodToken,
                    cardExpiry: verifyResult.expirationDate || '12/99',
                    last4CC: verifyResult.last4 || '0000',
                    merchant: 'PLACETOPAY',
                  }
                ],
              }
            ],
          };

          // Fire the new modifyAccountById mutation
          this.props.modifyAccountById(payload, () => {
            this.setState({ isSubmitting: false, isTokenFromPlaceToPay: false });
            this.props.history.push('/manage-credit-card');
          });

        } else {
          alert('PlaceToPay session was not approved or no token was generated.');
          this.setState({ isSubmitting: false, isTokenFromPlaceToPay: false });
        }
      })
      .catch((err) => {
        console.error('Failed to verify PlaceToPay tokenization:', err);
        alert('An error occurred verifying the saved card.');
        this.setState({ isSubmitting: false, isTokenFromPlaceToPay: false });
      });
  };

  /**
   * Initiates the PlaceToPay tokenization redirect.
   */
  onAddCardPlaceToPay = () => {
    const { account } = this.props;
    const contacts = account?.contacts || [];
    const contact = contacts[0] || {};
    const addresses = account?.addresses || [];
    const address = addresses[0] || {};
    const email = contact.email || 'test.user@embrix.com';

    const body = {
      reference: `Embrix-Setup-${Date.now()}`,
      description: 'Save Card for Jasec SelfCare',
      returnUrl: `${window.location.origin}/manage-credit-card`,
      buyerEmail: email,
      locale: 'en_US',
    };

    createPlaceToPayTokenize(body)
      .then((session) => {
        if (!session || !session.processUrl) throw new Error('No processUrl returned');

        // Store the requestId and the Redux payload data so we don't
        // have to wait for Redux to reload when we return
        const pendingData = {
          requestId: session.requestId,
          contact: {
            firstName: contact.firstName || '',
            lastName: contact.lastName || '',
            email: contact.email || '',
            number: contact.number || '',
          },
          address: {
            street: address.street || '',
            city: address.city || '',
            state: address.state || '',
            country: address.country || '',
            postalCode: address.postalCode || '',
          }
        };

        sessionStorage.setItem(P2P_TOKENIZE_KEY, JSON.stringify(pendingData));

        // Redirect to Evertec
        window.location.assign(session.processUrl);
      })
      .catch((err) => {
        console.error(err);
        alert('Could not start PlaceToPay session: ' + err.message);
      });
  };

  /**
   * Replaces the hardcoded logic in handleSubmit.
   * Updated to use modifyAccountById nested structure.
   */
  saveCardToLedger = (creditCardObject) => {
    const { modifyAccountById, account } = this.props;
    const existingProfileId = account?.paymentProfiles?.[0]?.id || 'PRIMARY';

    const payload = {
      id: getAccountId(),
      paymentProfiles: [
        {
          id: existingProfileId,
          paymentMethod: 'CREDIT_CARD',
          creditCards: [creditCardObject],
        }
      ],
    };

    modifyAccountById(payload, () => {
      this.setState({ isSubmitting: false });
      this.props.history.push('/manage-credit-card');
    });
  };

  toggle = () => {
    const { tooltipOpen } = this.state;
    this.setState({
      tooltipOpen: !tooltipOpen,
    });
  };

  componentWillReceiveProps(nextProps) {
    const { isTokenFromURL, isTokenFromPlaceToPay } = this.state;
    const { account } = nextProps;

    if (JSON.stringify(this.state.account) !== JSON.stringify(account)) {

      // If we are actively saving a PlaceToPay token, ignore incoming Redux
      // updates so our text boxes don't get wiped out.
      if (isTokenFromPlaceToPay) {
        this.setState({ account });
        return;
      }

      // Standard page load / Braintree logic
      if (!isTokenFromURL) {
        const paymentProfile = account?.paymentProfiles?.[0] || {};
        const creditCard = paymentProfile?.creditCards?.[0] || {};

        this.setState({
          account,
          cardToken: creditCard.cardToken,
          cardExpiry: creditCard.cardExpiry,
          last4CC: creditCard.last4CC,
          cardTokenFromAPI: creditCard.cardToken,
        });
        return;
      }

      this.setState({ account }, () => {
        this.handleSubmit();
        this.setState({ isTokenFromURL: false });
      });
    }
  }

  onDeleteCardToken = () => {
    const { cardToken } = this.state;
    const { modifyAccountById, account } = this.props;
    const existingProfileId = account?.paymentProfiles?.[0]?.id || 'PRIMARY';

    if (!cardToken) return;

    // Hide the modal and show loading state
    this.setState({ isOpenModalDelete: false, isSubmitting: true });

    // Call Payment Gateway to invalidate the token
    invalidatePlaceToPayToken({ paymentMethodToken: cardToken })
      .then((invalidateResult) => {
        if (invalidateResult && invalidateResult.successful) {

          // If successful, call GraphQL to reset the DB to CHECK
          const payloadDeleteToken = {
            id: getAccountId(),
            paymentProfiles: [
              {
                id: existingProfileId,
                paymentMethod: 'CHECK',
                creditCards: [], // CC array is now empty
              }
            ]
          };

          modifyAccountById(payloadDeleteToken, success => {
            if (success) {
              // Clear the UI
              this.setState({
                cardToken: '',
                cardExpiry: '',
                last4CC: '',
                cardTokenFromAPI: '',
                isSubmitting: false,
              });
              alert('Credit card successfully removed.');
            } else {
              alert('Token invalidated, but failed to update database.');
              this.setState({ isSubmitting: false });
            }
          });

        } else {
          alert(`Could not invalidate token: ${invalidateResult?.status?.message || 'Unknown error'}`);
          this.setState({ isSubmitting: false });
        }
      })
      .catch((error) => {
        console.error('Error invalidating token:', error);
        alert(`Error removing card: ${error.message}`);
        this.setState({ isSubmitting: false });
      });
  };

  onChangeText = evt => {
    let nameCardFocus = '';
    if (evt.target.name === 'last4CC') {
      nameCardFocus = 'cvc';
      this.setState({ focusCard: nameCardFocus });
      if (evt.target.value.length > 4) return;
    } else {
      this.setState({ focusCard: '' });
    }
    this.setState({
      [evt.target.name]: evt.target.value,
    });
  };

  renderCreditCard() {
    const { cardToken, cardExpiry, last4CC, hadPressSubmit, cardTokenFromAPI } = this.state;
    const { modeModifyCreditCard, modeAddCreditCard, t } = this.props;

    const domCardExpiry = document.querySelector('.rccs__expiry__valid');
    if (domCardExpiry) document.querySelector('.rccs__expiry__valid').textContent = 'Expiry';

    return (
      <div className="col-md-8 d-flex flex-wrap no-padding">
        <div className="row col-md-12 no-padding">
          <Col md={7}>
            <FormGroup>
              <Label for="cardToken">{`${t('label.creditCardToken')}:`}</Label>
              <div className="group-card-token">
                <Input
                  value={cardToken || ''}
                  type="text"
                  className="col-md-7"
                  name="cardToken"
                  id="cardToken"
                  disabled={modeModifyCreditCard === 1 || modeAddCreditCard === 1}
                  placeholder="Card Number"
                  onChange={this.onChangeText}
                />
                <button
                  className="button-delete-card"
                  type="button"
                  id="remove-card-token"
                  disabled={!cardTokenFromAPI || modeModifyCreditCard === 1 || modeAddCreditCard === 1}
                  onClick={() => {
                    this.setState({ isOpenModalDelete: true });
                  }}
                >
                  <FaRegTrashAlt />
                  <Tooltip
                    placement="top"
                    isOpen={this.state.tooltipOpen}
                    autohide={false}
                    target="remove-card-token"
                    toggle={this.toggle}
                  >
                    {t('label.removeCreditCard')}
                  </Tooltip>
                </button>
                <div className="col-md-5">
                  {/*
                  This flow is currently broken and adheres to Fiserv/Braintree.
                  Currently toggled off for PlaceToPay since this is what Jasec uses.

                  <ButtonCustom
                    className="btn-submit pl-2"
                    type="button"
                    disabled={modeModifyCreditCard === 1 || modeAddCreditCard === 1}
                    style={{ paddingLeft: 3 }}
                    title={t('label.addCreditCard')}
                    titleloading="Modifying"
                    datastyle={SLIDE_UP}
                    onClick={() => {
                      window.open(`${urlGetCreditCard.URL}${window.location}`, '_self');
                    }}
                  />
                  */}

                  {/* PLACETOPAY BUTTON */}
                  <ButtonCustom
                    className="btn-submit pl-2 ml-2"
                    type="button"
                    disabled={modeModifyCreditCard === 1 || modeAddCreditCard === 1}
                    title={t('label.saveWithPlaceToPay')}
                    onClick={this.onAddCardPlaceToPay}
                  />
                </div>
              </div>

              <FormFeedback valid={!hadPressSubmit}>
                <b>{t('label.creditCardNumber')}</b>
                {' '}
                {t('message.canNotBeBlank')}
              </FormFeedback>
            </FormGroup>
          </Col>
          <Col md={5}>
            <FormGroup>
              <Label for="last4CC">{`${t('label.creditCardCVV')}:`}</Label>
              <Input
                type="text"
                disabled={modeModifyCreditCard === 1}
                name="last4CC"
                id="last4CC"
                placeholder="Last 4CC"
                value={last4CC || ''}
                onChange={this.onChangeText}
              />
              <FormFeedback valid={!hadPressSubmit}>{t('message.cvvMsg')}</FormFeedback>
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="cardExpiry">{`${t('label.creditCardExpiry')}:`}</Label>
              <Input
                type="text"
                name="cardExpiry"
                id="cardExpiry"
                placeholder="MM/YYYY"
                value={cardExpiry || ''}
                disabled={modeModifyCreditCard === 1}
                onChange={this.onChangeText}
              />
              <FormFeedback valid={!hadPressSubmit}>
                <b>{t('label.creditCardExpiry')}</b>
                {' '}
                {t('message.canNotBeBlank')}
              </FormFeedback>
            </FormGroup>
          </Col>
        </div>
      </div>
    );
  }

  handleSubmit = evt => {
    if (evt) evt.preventDefault();
    const { cardToken, cardExpiry, last4CC, merchantToSave } = this.state;

    this.saveCardToLedger({
      cardToken,
      cardExpiry,
      last4CC,
      merchant: merchantToSave || 'qabrain3', // qabrain3 hardcoded for Fiserv/Braintree
    });
  };

  render() {
    const { isSubmitting, cardToken, cardExpiry, focusCard, last4CC, isOpenModalDelete } = this.state;
    const { modeModifyCreditCard, t } = this.props;
    return (
      <Form>
        <div className="col-md-12 row no-padding">
          {this.renderCreditCard()}
          <div className="col-md-4">
            <Cards
              cvc={last4CC || ''}
              expiry={cardExpiry || ''}
              focused={focusCard}
              name={`Token: ${cardToken || ''}`}
              number=""
            />
          </div>
        </div>

        {/*
        // Currently, we automatically submit once client finish his session with P2P, Braintree, etc
        // Consider a logic to delete the card instead
        <Row className="mt-sm-3 mx-0">
          <Col sm={{ size: 12, offset: 8 }} className="row float-right mt-5">
            <ButtonCustom
              className="btn-cancel ml-auto mt-auto mb-auto mr-2"
              type="button"
              title={t('label.cancel')}
              titleloading="Modifying"
              datastyle={SLIDE_UP}
              onClick={() => {
                this.props.history.push('/');
              }}
            />
            {modeModifyCreditCard !== 0 && (
              <ButtonCustom
                loading={isSubmitting}
                className="btn-submit"
                type="submit"
                title={t('label.submit')}
                titleloading="Modifying"
                disabled={modeModifyCreditCard === 1}
                datastyle={SLIDE_UP}
                onClick={this.handleSubmit}
              />
            )}
          </Col>
        </Row>
        */}
        <ModalAccept
          onAcceptDelete={this.onDeleteCardToken}
          isOpen={isOpenModalDelete}
          title={t('message.msgDeleteToken')}
          onToggle={() => {
            this.setState({
              isOpenModalDelete: !isOpenModalDelete,
            });
          }}
        />
      </Form>
    );
  }
}

ManageCreditCard.propTypes = {
  modifyAccountById: PropTypes.func,
  account: PropTypes.object,
  history: PropTypes.object,
  modeModifyCreditCard: PropTypes.number,
  modeAddCreditCard: PropTypes.number,
};

export default withTranslation('common')(ManageCreditCard);